import JwtBaseClient from './JwtBaseClient';

/**
 * @class OffersNegotiationClient
 * @extends JwtBaseClient
 * @description Client used to interface with Offers and Negotiation services.
 * JWT authentication and error handling are managed by JwtBaseClient.
 */
class OffersNegotiationClient extends JwtBaseClient {
  constructor() {
    const pathToApiErrors = 'response.data.error.message';
    super(import.meta.env.VITE_OFFERS_AND_NEGOTIATION, pathToApiErrors);
  }

  // Method to delete a deal by ID
  async deleteDeal(id) {
    const response = await this.delete(`/api/v1/deals/${id}`);
    return response.data;
  }

  async getDeals({
    auctionIds, isSeller, size = 10, start = 0, pagination = true, filterStatusCount = true,
  }) {
    const params = {
      size,
      start,
      auction_ids: auctionIds,
      is_seller: isSeller,
      pagination,
      filter_status_count: filterStatusCount,
    };
    const response = await this.get('/api/v1/deals', params);
    if (
      response.data
      && response.data.data
      && Array.isArray(response.data.data.deals)
      && response.data.data.deals.length > 0
      && response.data.data.deals[0].id
    ) {
      return response.data.data.deals[0].id;
    }
    return null;
  }

  async getHighestRejectedOffer(auctionId) {
    const response = await this.get(`/api/v1/auctions/highest-rejected-offer/${auctionId}`);
    // Return response wrapped for consistency with getHighestRejectedOffers
    return { data: response.data };
  }

  getHighestRejectedOffers(auctionIds, useSavedAuctionIds = false) {
    const payload = useSavedAuctionIds
      ? { saved_auction_ids: auctionIds }
      : { auction_ids: auctionIds };

    return this.post(
      '/api/v1/auctions/highest-rejected-offer/list',
      payload,
    );
    // Wrap response to match expected format: { data: { restriction_offers: [...] } }
  }

  getAttributes(attributes, auctionIds, useSavedAuctionIds = false) {
    const payload = useSavedAuctionIds
      ? { attributes, saved_auction_ids: auctionIds }
      : { attributes, auction_ids: auctionIds };

    return this.post('/api/v1/auctions/attributes/list', payload);
  }

  // NOTE on return shape: the four ended-auction-purchase / delete methods
  // below all unwrap and return `response.data` (the parsed API body), unlike
  // some older methods above in this file (e.g. getHighestRejectedOffers,
  // getAttributes) which still return the full, un-unwrapped axios response.
  // This is a deliberate, scoped convention for this endpoint family — not
  // yet a standard applied across the whole client — so check a method's
  // actual return value rather than assuming based on a sibling method.
  async createEndedAuctionPurchaseAmount(auctionId, amount) {
    const response = await this.post(`/api/v1/auctions/${auctionId}/ended-auction-purchase/amount`, { amount });
    return response.data;
  }

  async acceptEndedAuctionPurchase(auctionId, amount) {
    const response = await this.post(`/api/v1/auctions/${auctionId}/ended-auction-purchase/accept`, { amount });
    return response.data;
  }

  async deleteEndedAuctionPurchaseAmount(auctionId, isSavedAuction = false) {
    const path = isSavedAuction
      ? `/api/v1/saved_auctions/${auctionId}/ended-auction-purchase/amount`
      : `/api/v1/auctions/${auctionId}/ended-auction-purchase/amount`;
    const response = await this.delete(path);
    return response.data.data;
  }

  async deleteHighestRejectedOffer(auctionId, isSavedAuction = false) {
    const params = isSavedAuction ? { saved_auction_id: true } : {};
    const response = await this.delete(
      `/api/v1/auctions/highest-rejected-offer/${auctionId}`,
      null, // data
      null, // headers
      params, // query params
    );
    return response.data;
  }

  async getOffersForVins({ vin, dealerIds }) {
    const params = {
      vin,
    };

    if (dealerIds) {
      params.dealer_ids = Array.isArray(dealerIds) ? dealerIds.join(',') : dealerIds;
    }

    const response = await this.get('/api/v1/offers_for_vins', params);
    return response.data;
  }
}

export default OffersNegotiationClient;
